import api from "./api";
import { ok, isoUtc, nameOf, reasonOf } from "./core";

export const EVIDENCE_CONTEXT_LABEL = { GOAL: "Goal", KRA: "KRA", KPI: "KPI", SELF_ASSESSMENT: "Self assessment", MANAGER_ASSESSMENT: "Manager assessment" };

export const ATTACHMENT_TYPES = [
  { value: "", label: "Any type" },
  { value: "URL", label: "Links" },
  { value: "FILE", label: "Files" },
  { value: "PDF", label: "PDF files" },
  { value: "IMAGE", label: "Images" },
  { value: "TEXT", label: "Text files" },
];

export function linkProblem(url) {
  const value = String(url || "").trim();
  if (!value) return "Paste the link.";
  if (/\s/.test(value)) return "Links can't contain spaces.";
  let parsed;
  try { parsed = new URL(value); } catch { return "Use a full link starting with https://"; }
  if (!["http:", "https:"].includes(parsed.protocol)) return "Only http(s) links are allowed.";
  if (parsed.username || parsed.password) return "Remove the user name / password from the link.";
  return null;
}

export async function addEvidenceLink(recordId, { url, title, description, itemId, context = "SELF_ASSESSMENT", reason }) {
  const problem = linkProblem(url);
  if (problem) throw new Error(problem);
  if (!String(title || "").trim()) throw new Error("Give the link a short title.");
  const { data } = await api.post(`/evidence/performance/${recordId}/links`, {
    url: String(url).trim(),
    title: String(title).trim(),
    description: String(description || "").trim(),
    item_id: itemId ? Number(itemId) : null,
    context,
    reason: reasonOf(reason, "Evidence link added"),
  });
  return ok(data);
}

const toEvidence = (e) => ({
  key: `${e.evidence_type}-${e.evidence_id}`,
  type: e.evidence_type,
  id: e.evidence_id,
  title: e.title,
  description: e.description || "",
  fileName: e.filename,
  mimeType: e.mime_type,
  url: e.url,
  scanStatus: e.scan_status,
  context: EVIDENCE_CONTEXT_LABEL[e.evidence_context] || e.evidence_context,
  recordId: e.performance_record_id,
  cycleId: e.cycle_id,
  employeeId: e.employee_id,
  employeeName: e.employee_name || nameOf(e.employee_id),
  itemId: e.performance_item_id,
  itemTitle: e.item_title || "",
  itemType: e.item_type || "",
  // [NEW] PM-7: library ids, so the Evidence page can show separate KRA, KPI and Goal columns
  kraId: e.kra_id ? String(e.kra_id) : "",
  kpiId: e.kpi_id ? String(e.kpi_id) : "",
  addedBy: nameOf(e.added_by_employee_id),
  addedAt: isoUtc(e.added_at),
});

// [UPDATED] PM-18: itemType, kraId and attachmentType may be lists (checkbox filters). The backend takes one value
// per filter, so with several values one search runs per combination and the results are merged and paged here.
const asList = (v) => (Array.isArray(v) ? v.filter((x) => x !== "" && x !== null && x !== undefined) : v ? [v] : []);
const MERGE_LIMIT = 200;

export async function searchEvidence(filters = {}) {
  const lists = { itemType: asList(filters.itemType), kraId: asList(filters.kraId), attachmentType: asList(filters.attachmentType) };
  if (Object.values(lists).every((l) => l.length <= 1)) {
    return searchEvidenceOnce({ ...filters, itemType: lists.itemType[0], kraId: lists.kraId[0], attachmentType: lists.attachmentType[0] });
  }
  const combos = [{}];
  Object.entries(lists).forEach(([key, values]) => {
    const next = [];
    combos.forEach((c) => (values.length ? values : [undefined]).forEach((v) => next.push({ ...c, [key]: v })));
    combos.splice(0, combos.length, ...next);
  });
  const results = await Promise.all(combos.map((c) => searchEvidenceOnce({ ...filters, ...c, limit: MERGE_LIMIT, offset: 0 })));
  const seen = new Map();
  results.forEach((r) => r.data.items.forEach((item) => seen.set(item.key, item)));
  const all = [...seen.values()].sort((a, b) => String(b.addedAt).localeCompare(String(a.addedAt)));
  const offset = filters.offset || 0;
  return ok({ items: all.slice(offset, offset + (filters.limit || 50)), total: all.length });
}

async function searchEvidenceOnce(filters) {
  const { data } = await api.get("/evidence/search", {
    params: {
      q: filters.q, employee_id: filters.employeeId, record_id: filters.recordId, cycle_id: filters.cycleId, item_id: filters.itemId,
      item_type: filters.itemType, kra_id: filters.kraId, context: filters.context, attachment_type: filters.attachmentType,
      date_from: filters.dateFrom, date_to: filters.dateTo, limit: filters.limit || 50, offset: filters.offset || 0,
    },
  });
  return ok({ items: data.items.map(toEvidence), total: data.total });
}
